import React, { useState, useEffect } from 'react';
import { Layout, Card, Spin, Alert, Typography } from 'antd';
import { 
  FolderOutlined, 
  FolderOpenOutlined, 
  FileTextOutlined,
  CaretRightOutlined,
  CaretDownOutlined
} from '@ant-design/icons';
import SourceCodeViewer from './SourceCodeViewer';
import { TreeStructure, ProjectInfo, ApiResponse, TreeNode } from '../types';
import './ProjectExplorer.css';

const { Sider, Content } = Layout;
const { Text } = Typography;

const ProjectExplorer: React.FC = () => {
  const [treeData, setTreeData] = useState<TreeStructure | null>(null);
  const [projectInfo, setProjectInfo] = useState<ProjectInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<TreeNode | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchProjectStructure();
  }, []);

  const fetchProjectStructure = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://mini.easy2hub.com/cov/api/projects/project1/structure');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data: ApiResponse = await response.json();
      setProjectInfo(data.projectInfo);
      setTreeData(data.treeStructure);
      
      // 默认展开根节点
      setExpandedNodes(new Set(['com']));
    } catch (err: any) {
      setError(err.message || 'Failed to fetch project structure');
    } finally {
      setLoading(false);
    }
  };

  const toggleNode = (nodeId: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const selectNode = (node: TreeNode) => {
    if (node.type === 'file') {
      setSelectedFile(node);
    }
  };

  const getCoverageColor = (coverageRate: number): string => {
    if (coverageRate >= 80) return '#52c41a';  // 绿色 - 高覆盖率
    if (coverageRate >= 60) return '#faad14';  // 黄色 - 中等覆盖率
    if (coverageRate > 0) return '#ff4d4f';    // 红色 - 低覆盖率
    return '#ff1744';                          // 鲜红色 - 无覆盖率
  };

  const calculateOverallCoverage = (node: TreeNode): number => {
    if (node.coverageStats && node.coverageStats.testTypes) {
      return node.coverageStats.coverageRate;
    }
    return 0;
  };

  const renderTreeNode = (node: TreeNode): React.ReactNode => {
    if (!treeData) return null;

    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children.length > 0;
    const isSelected = selectedFile?.id === node.id;

    return (
      <div key={node.id} className="tree-node">
        <div 
          className={`node-content ${isSelected ? 'selected' : ''}`}
          onClick={() => {
            if (node.type === 'package' && hasChildren) {
              toggleNode(node.id);
            } else {
              selectNode(node);
            }
          }}
          style={{ paddingLeft: `${node.level * 16 + 8}px` }}
        >
          <div className="node-expand-icon">
            {hasChildren ? (
              <span 
                className="expand-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleNode(node.id);
                }}
              >
                {isExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
              </span>
            ) : (
              <span className="expand-placeholder" />
            )}
          </div>
          
          <div className="node-icon">
            {node.type === 'package' ? (
              isExpanded ? <FolderOpenOutlined /> : <FolderOutlined />
            ) : (
              <FileTextOutlined />
            )}
          </div>
          
          <div className="node-name">
            <Text className="node-text">{node.name}</Text>
          </div>
          
          <div className="node-coverage">
            {(() => {
              const overallCoverage = calculateOverallCoverage(node);
              return overallCoverage > 0 && (
                <div 
                  className="coverage-indicator"
                  style={{ backgroundColor: getCoverageColor(overallCoverage) }}
                  title={`Overall Coverage: ${overallCoverage.toFixed(1)}%`}
                />
              );
            })()}
          </div>
        </div>
        
        {isExpanded && hasChildren && (
          <div className="node-children">
            {node.children.map(childId => {
              const childNode = treeData.nodes.find(n => n.id === childId);
              return childNode ? renderTreeNode(childNode) : null;
            })}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="project-explorer-loading">
        <Spin size="large" tip="Loading project structure..." />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        action={
          <button onClick={fetchProjectStructure}>
            Retry
          </button>
        }
      />
    );
  }

  if (!treeData || !projectInfo) {
    return <div>No data available</div>;
  }

  return (
    <Layout className="project-explorer">
      <Sider width={400} className="explorer-sidebar">
        <Card 
          title={
            <div className="project-header">
              <Text strong>{projectInfo.name}</Text>
              <Text type="secondary" style={{ fontSize: '12px', marginLeft: '8px' }}>
                v{projectInfo.version}
              </Text>
            </div>
          }
          size="small"
          className="explorer-card"
        >
          <div className="tree-container">
            {renderTreeNode(treeData.root)}
          </div>
        </Card>
      </Sider>
      
      <Content className="explorer-content">
        {selectedFile && selectedFile.type === 'file' ? (
          <SourceCodeViewer
            projectId="project1"
            commitHash="abc123"
            filePath={selectedFile.filePath?.replace('src/main/java/', '').replace('.java.java', '.java') || ''}
          />
        ) : (
          <Card 
            title="File Details"
            size="small"
            className="details-card"
          >
            {selectedFile ? (
              <div className="file-details">
                <div className="file-info">
                  <Text strong>File: </Text>
                  <Text code>{selectedFile.name}</Text>
                </div>
                <div className="file-info">
                  <Text strong>Path: </Text>
                  <Text code>{selectedFile.filePath}</Text>
                </div>
                <div className="file-info">
                  <Text strong>Type: </Text>
                  <Text>{selectedFile.type}</Text>
                </div>
                <div className="file-info">
                  <Text strong>Overall Coverage: </Text>
                  <Text 
                    style={{ 
                      color: getCoverageColor(calculateOverallCoverage(selectedFile)),
                      fontWeight: 'bold'
                    }}
                  >
                    {calculateOverallCoverage(selectedFile).toFixed(1)}%
                  </Text>
                  <Text type="secondary" style={{ marginLeft: '8px' }}>
                    ({selectedFile.coverageStats.coveredLines}/{selectedFile.coverageStats.totalLines} lines)
                  </Text>
                </div>
                <div className="file-info">
                  <Text strong>Test Types: </Text>
                  <Text>{selectedFile.coverageStats.testTypes.join(', ')}</Text>
                </div>
              </div>
            ) : (
              <div className="no-selection">
                <Text type="secondary">Select a file to view source code and coverage</Text>
              </div>
            )}
          </Card>
        )}
      </Content>
    </Layout>
  );
};

export default ProjectExplorer;
